import { useMutation, useQuery } from "@apollo/client";
import { compact, omit } from "lodash";
import { useCallback, useMemo, useState } from "react";
import {
  AssessmentGroupFragmentFragment,
  AssessmentGroupQuestionInput,
  AssessmentQuestionComment,
  AssessmentQuestionResponseVisibility,
  AssessmentQuestionResponses,
  AssessmentQuestionType,
  AssessmentType,
  FullAssessmentGroupSectionInput,
  GetAssessmentGroupForSidebarQuery,
  GetAssessmentGroupForSidebarQueryVariables,
  LoggedInUserOrgFragment,
  SaveAssessmentGroupMutation,
  SaveAssessmentGroupMutationVariables,
  SaveFullAssessmentGroupMutation,
  SaveFullAssessmentGroupMutationVariables,
} from "types/graphql-schema";

import createOrUpdateAssessmentGroupMutation from "@apps/assessments/graphql/create-or-update-assessment-group-mutation";
import createOrUpdateFullAssessmentGroupMutation from "@apps/programs/graphql/create-or-update-full-assessment-group-mutation";
import getAssessmentGroupForSidebar from "@apps/programs/graphql/get-assessment-group-for-sidebar-query";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Dropdown from "@components/dropdown/dropdown";
import FormFields from "@components/form/form-fields";
import Input from "@components/input/input";
import Loading from "@components/loading/loading";
import Sidebar from "@components/sidebar/sidebar";
import { assertNonNull } from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";

import AssessmentGroupSidebarSection from "./assessment-group-sidebar-section";

const emptyQuestion = (
  questionType: AssessmentQuestionType,
): AssessmentGroupQuestionInput => ({
  questionType: AssessmentQuestionType.Range,
  responseVisibility: AssessmentQuestionResponseVisibility.All,
  responses: AssessmentQuestionResponses.Both,
  comment:
    questionType === AssessmentQuestionType.Text
      ? AssessmentQuestionComment.NoComment
      : AssessmentQuestionComment.Optional,
  title: "",
  startValue: questionType === AssessmentQuestionType.Range ? 1 : undefined,
  endValue: questionType === AssessmentQuestionType.Range ? 3 : undefined,
  labels:
    questionType === AssessmentQuestionType.Range ? ["", "", ""] : undefined,
  labelDescriptions:
    questionType === AssessmentQuestionType.Range ? ["", "", ""] : undefined,
  options:
    questionType === AssessmentQuestionType.Multichoice ? ["", ""] : undefined,
  optionDescriptions:
    questionType === AssessmentQuestionType.Multichoice ? ["", ""] : undefined,
});

const parseExistingAssessmentGroup = (
  organization: LoggedInUserOrgFragment,
  assessmentGroup: Omit<AssessmentGroupFragmentFragment, "id" | "sections"> & {
    id?: number | null;
    sections: {
      edges: ReadonlyArray<{
        node?: {
          title?: string | null;
          description?: string | null;
          questions: {
            edges: ReadonlyArray<{
              node?: {
                id?: number | null;
                question: {
                  readonly __typename: string;
                  readonly id?: number | null;
                  readonly title: string;
                  readonly description?: string | null;
                  readonly comment: AssessmentQuestionComment;
                  readonly responses: AssessmentQuestionResponses;
                  readonly responseVisibility: AssessmentQuestionResponseVisibility;
                  readonly questionType: AssessmentQuestionType;
                  readonly startValue?: number;
                  readonly endValue?: number;
                  readonly labels?: ReadonlyArray<string | null>;
                  readonly labelDescriptions?: ReadonlyArray<string | null>;
                  readonly options?: ReadonlyArray<string | null>;
                  readonly optionDescriptions?: ReadonlyArray<string | null>;
                };
              } | null;
            } | null>;
          };
        } | null;
      } | null>;
    };
  },
) => ({
  title: assessmentGroup.title,
  organizationId: organization.id,
  assessmentGroupId: assessmentGroup.id,
  assessmentType: assessmentGroup.assessmentType,
  sections: assessmentGroup.sections.edges.map((sectionEdge) => ({
    title: assertNonNull(sectionEdge?.node).title,
    description: assertNonNull(sectionEdge?.node).description,
    questions: assertNonNull(sectionEdge?.node).questions.edges.map(
      (questionEdge) => {
        const question = assertNonNull(questionEdge?.node?.question);
        return {
          id: question.id,
          title: question.title,
          description: question.description,
          startValue:
            question.__typename === "RangeAssessmentQuestionNode"
              ? question.startValue
              : undefined,
          endValue:
            question.__typename === "RangeAssessmentQuestionNode"
              ? question.endValue
              : undefined,
          comment: question.comment,
          labels:
            question.__typename === "RangeAssessmentQuestionNode"
              ? question.labels
              : undefined,
          labelDescriptions:
            question.__typename === "RangeAssessmentQuestionNode"
              ? question.labelDescriptions
              : undefined,
          options:
            question.__typename === "MultiChoiceAssessmentQuestionNode"
              ? question.options
              : undefined,
          optionDescriptions:
            question.__typename === "MultiChoiceAssessmentQuestionNode"
              ? question.optionDescriptions
              : undefined,
          responses: question.responses,
          responseVisibility: question.responseVisibility,
          questionType: question.questionType,
        };
      },
    ),
  })),
});

const AssessmentGroupSidebar = ({
  onClose,
  assessmentGroupId,
  assessmentGroupToDuplicateId,
  onAssessmentGroupCreated,
  onAssessmentGroupArchived,
  assessmentType,
}: {
  assessmentGroupId?: number | null;
  assessmentGroupToDuplicateId?: number | null;
  onClose: () => void;
  onAssessmentGroupCreated: (
    newAssessmentGroup: AssessmentGroupFragmentFragment,
  ) => void;
  onAssessmentGroupArchived: (assessmentGroupId: number) => void;
  assessmentType: AssessmentType;
}) => {
  const label = useLabel();
  const organization = currentOrganizationVar();
  const [isDuplicating, setIsDuplicating] = useState(false);

  // Fetch assessment group data if an ID is provided
  const { data: assessmentGroupData, loading: loadingAssessmentGroup } =
    useQuery<
      GetAssessmentGroupForSidebarQuery,
      GetAssessmentGroupForSidebarQueryVariables
    >(getAssessmentGroupForSidebar, {
      skip: !assessmentGroupId || isDuplicating,
      variables: { assessmentGroupId: assessmentGroupId || 0 },
    });

  // Fetch assessment group to duplicate if an ID is provided
  const {
    data: assessmentGroupToDuplicateData,
    loading: loadingAssessmentGroupToDuplicate,
  } = useQuery<
    GetAssessmentGroupForSidebarQuery,
    GetAssessmentGroupForSidebarQueryVariables
  >(getAssessmentGroupForSidebar, {
    skip: !assessmentGroupToDuplicateId,
    variables: { assessmentGroupId: assessmentGroupToDuplicateId || 0 },
  });

  const assessmentGroup = assessmentGroupData?.assessmentGroup;
  const assessmentGroupToDuplicate =
    assessmentGroupToDuplicateData?.assessmentGroup;
  const isLoading = loadingAssessmentGroup || loadingAssessmentGroupToDuplicate;

  const [proposedAssessmentGroup, setProposedAssessmentGroup] =
    useState<SaveFullAssessmentGroupMutationVariables>({
      title: "",
      organizationId: organization.id,
      sections: [{ questions: [] }],
      assessmentType,
    });

  // Initialize proposed assessment group when data is loaded
  useMemo(() => {
    if (isLoading) return;

    if (assessmentGroupToDuplicate) {
      setProposedAssessmentGroup(
        // we need to remove all the IDs when duplicating
        parseExistingAssessmentGroup(organization, {
          ...omit(assessmentGroupToDuplicate, ["id"]),
          sections: {
            ...assessmentGroupToDuplicate.sections,
            edges: [
              ...assessmentGroupToDuplicate.sections.edges.map((edge) => {
                const node = assertNonNull(edge?.node);
                return {
                  ...assertNonNull(edge),
                  node: {
                    ...omit(node, ["id"]),
                    questions: {
                      ...node.questions,
                      edges: [
                        ...node.questions.edges.map((questionEdge) => {
                          return {
                            ...questionEdge,
                            node: {
                              ...questionEdge?.node,
                              question: omit(questionEdge?.node?.question, [
                                "id",
                              ]),
                            },
                          };
                        }),
                      ],
                    },
                  },
                };
              }),
            ],
          },
          title: `Copy of ${assessmentGroupToDuplicate.title}`,
        }),
      );
    } else if (assessmentGroup) {
      setProposedAssessmentGroup(
        parseExistingAssessmentGroup(organization, assessmentGroup),
      );
    }
  }, [assessmentGroup, assessmentGroupToDuplicate, isLoading, organization]);

  const [saveAssessmentGroup, { loading: isSaving }] = useMutation<
    SaveFullAssessmentGroupMutation,
    SaveFullAssessmentGroupMutationVariables
  >(createOrUpdateFullAssessmentGroupMutation);
  const [createOrUpdateAssessmentGroup, { loading: isArchivingGroup }] =
    useMutation<
      SaveAssessmentGroupMutation,
      SaveAssessmentGroupMutationVariables
    >(createOrUpdateAssessmentGroupMutation);

  const { ConfirmationDialog, confirm } = useConfirm(
    "Are you sure you want to archive this question set?",
    `You will no longer be able to use it anymore. Existing ${label("review", {
      pluralize: true,
    })} and surveys will not be affected.`,
  );

  const handleSave = useCallback(() => {
    saveAssessmentGroup({
      variables: proposedAssessmentGroup,
      onCompleted: (response) => {
        successNotificationVar({
          title: "Question set saved successfully",
          timeout: 2000,
        });
        onAssessmentGroupCreated(
          assertNonNull(
            response.createOrUpdateFullAssessmentGroup?.assessmentGroup,
          ),
        );
        onClose();
      },
    });
  }, [
    onAssessmentGroupCreated,
    proposedAssessmentGroup,
    saveAssessmentGroup,
    onClose,
  ]);

  const handleArchive = useCallback(async () => {
    const confirmation = await confirm();
    if (!proposedAssessmentGroup.assessmentGroupId || !confirmation) {
      return;
    }
    createOrUpdateAssessmentGroup({
      variables: {
        assessmentGroupId: proposedAssessmentGroup.assessmentGroupId,
        archived: true,
      },
      onCompleted: () => {
        successNotificationVar({
          title: "Question set archived",
          timeout: 2000,
        });
        onAssessmentGroupArchived(
          assertNonNull(proposedAssessmentGroup.assessmentGroupId),
        );
        onClose();
      },
    });
  }, [
    confirm,
    proposedAssessmentGroup,
    createOrUpdateAssessmentGroup,
    onAssessmentGroupArchived,
    onClose,
  ]);

  const handleDuplicate = useCallback(() => {
    setIsDuplicating(true);
    setProposedAssessmentGroup({
      ...proposedAssessmentGroup,
      assessmentGroupId: undefined,
      title: `Copy of ${proposedAssessmentGroup.title ?? "Unnamed"}`,
    });
  }, [proposedAssessmentGroup]);

  const sections = useMemo(() => {
    return proposedAssessmentGroup.sections as FullAssessmentGroupSectionInput[];
  }, [proposedAssessmentGroup]);

  const handleAddSection = useCallback(
    (sectionIndex: number) => {
      const newSection = { questions: [] };
      const newSectionList = [
        ...sections.slice(0, sectionIndex + 1),
        newSection,
        ...sections.slice(sectionIndex + 1),
      ];
      setProposedAssessmentGroup((prev) => ({
        ...prev,
        sections: newSectionList,
      }));
    },
    [sections],
  );

  const handleRemoveSection = useCallback(
    (sectionIndex: number) => {
      setProposedAssessmentGroup((prev) => {
        const newSections = [...sections];
        newSections.splice(sectionIndex, 1);
        return { ...prev, sections: newSections };
      });
    },
    [sections],
  );

  const handleAddQuestion = useCallback(
    (sectionIndex: number, questionType: AssessmentQuestionType) => {
      const newSection = {
        ...sections[sectionIndex],
        questions: [
          ...sections[sectionIndex].questions,
          { ...emptyQuestion(questionType), questionType },
        ],
      };
      setProposedAssessmentGroup((prev) => ({
        ...prev,
        sections: [
          ...sections.slice(0, sectionIndex),
          newSection,
          ...sections.slice(sectionIndex + 1),
        ],
      }));
    },
    [sections],
  );

  const handleScrollIntoView = useCallback(
    (sectionIndex: number, questionIndex?: number) => {
      setTimeout(() => {
        const questionElement =
          questionIndex !== undefined
            ? document.getElementById(
                `question-${sectionIndex}-${questionIndex}`,
              )
            : document.getElementById(`section-${sectionIndex}`);
        if (questionElement) {
          questionElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
          questionElement.classList.add("flash-assessment-question");
          setTimeout(() => {
            questionElement.classList.remove("flash-assessment-question");
          }, 2000);
        }
      }, 100);
    },
    [],
  );

  const handleDuplicateSection = useCallback(
    (sectionIndex: number) => {
      setProposedAssessmentGroup({
        ...proposedAssessmentGroup,
        sections: [
          ...sections.slice(0, sectionIndex),
          sections[sectionIndex],
          {
            ...sections[sectionIndex],
            title: `Copy of ${sections[sectionIndex].title ?? "Unnamed"}`,
          },
          ...sections.slice(sectionIndex + 1),
        ],
      });
      handleScrollIntoView(sectionIndex + 1);
    },
    [handleScrollIntoView, proposedAssessmentGroup, sections],
  );

  const handleMoveSection = useCallback(
    (sectionIndex: number, direction: "up" | "down") => {
      const newSections = [...sections];
      const sectionToMove = newSections.splice(sectionIndex, 1)[0];
      const toIndex = direction === "up" ? sectionIndex - 1 : sectionIndex + 1;
      newSections.splice(toIndex, 0, sectionToMove);
      setProposedAssessmentGroup({
        ...proposedAssessmentGroup,
        sections: newSections,
      });
      handleScrollIntoView(toIndex);
    },
    [handleScrollIntoView, proposedAssessmentGroup, sections],
  );

  const handleMoveQuestion = useCallback(
    (sectionIndex: number, questionIndex: number, direction: "up" | "down") => {
      const sections =
        proposedAssessmentGroup.sections as FullAssessmentGroupSectionInput[];

      let toSection: number;
      let toIndex: number;

      if (direction === "up") {
        toSection = questionIndex === 0 ? sectionIndex - 1 : sectionIndex;
        toIndex =
          questionIndex === 0
            ? sections[toSection]?.questions.length > 0
              ? sections[toSection].questions.length
              : 0
            : questionIndex - 1;
      } else {
        toSection =
          questionIndex === sections[sectionIndex].questions.length - 1
            ? sectionIndex + 1
            : sectionIndex;
        toIndex =
          questionIndex === sections[sectionIndex].questions.length - 1
            ? 0
            : questionIndex + 1;
      }

      const oldSectionQuestions = [...sections[sectionIndex].questions];
      const questionToMove = oldSectionQuestions.splice(questionIndex, 1)[0];

      if (toSection === sectionIndex) {
        // Question stays in same section
        oldSectionQuestions.splice(toIndex, 0, questionToMove);

        setProposedAssessmentGroup({
          ...proposedAssessmentGroup,
          sections: [
            ...sections.slice(0, sectionIndex),
            {
              ...sections[sectionIndex],
              questions: oldSectionQuestions,
            },
            ...sections.slice(sectionIndex + 1),
          ],
        });
      } else {
        // Question moves to different section
        const newSectionQuestions = [...sections[toSection].questions];
        newSectionQuestions.splice(toIndex, 0, questionToMove);

        const updatedSections = [...sections];
        updatedSections[sectionIndex] = {
          ...sections[sectionIndex],
          questions: oldSectionQuestions,
        };
        updatedSections[toSection] = {
          ...sections[toSection],
          questions: newSectionQuestions,
        };

        setProposedAssessmentGroup({
          ...proposedAssessmentGroup,
          sections: updatedSections,
        });
      }

      handleScrollIntoView(toSection, toIndex);
    },
    [handleScrollIntoView, proposedAssessmentGroup],
  );

  const handleMoveQuestionUp = useCallback(
    (sectionIndex: number, questionIndex: number) => {
      handleMoveQuestion(sectionIndex, questionIndex, "up");
    },
    [handleMoveQuestion],
  );

  const handleMoveQuestionDown = useCallback(
    (sectionIndex: number, questionIndex: number) => {
      handleMoveQuestion(sectionIndex, questionIndex, "down");
    },
    [handleMoveQuestion],
  );

  const canUpdateAssessmentGroup =
    !isArchivingGroup &&
    (!assessmentGroup ||
      !!assessmentGroupToDuplicate ||
      !!assessmentGroup?.canUpdate?.permission ||
      isDuplicating);

  const sectionSaveTooltip = sections.reduce(
    (warning, section) => {
      if (warning) {
        return warning;
      }
      return section.questions.some((question) => question?.title.trim() === "")
        ? "Please fill in all question labels"
        : section.questions.length === 0
          ? "Please add at least 1 question to each section"
          : section.questions
                .filter(
                  (question) =>
                    question?.questionType ===
                    AssessmentQuestionType.Multichoice,
                )
                .some((question) =>
                  question?.options?.some((opt) => opt?.trim() === ""),
                )
            ? "All multiple choice options need a label"
            : null;
    },
    null as string | null,
  );

  const saveTooltip =
    proposedAssessmentGroup.title?.trim() === ""
      ? "Please enter a title"
      : sectionSaveTooltip
        ? sectionSaveTooltip
        : undefined;

  if (isLoading) {
    return (
      <Sidebar
        wide
        className="bg-gray-50"
        headerClassName="bg-gray-50"
        onClose={onClose}
      >
        <div className="flex items-center justify-center h-64">
          <Loading>Loading Question Set...</Loading>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar
      wide
      title={`${
        proposedAssessmentGroup.assessmentGroupId ? "Edit" : "New"
      } Question Set`}
      className="bg-gray-50"
      headerClassName="bg-gray-50"
      onClose={onClose}
      additionalActions={
        <>
          <Button
            disabled={isSaving}
            theme={buttonTheme.text}
            text="Discard"
            onClick={onClose}
          />
          <Button
            disabled={isSaving || !!saveTooltip}
            tooltip={saveTooltip}
            theme="primary"
            text="Save"
            onClick={handleSave}
          />
          <Dropdown
            disabled={isArchivingGroup}
            options={compact([
              {
                label: "Duplicate this question set",
                onClick: handleDuplicate,
              },
              proposedAssessmentGroup.assessmentGroupId && {
                label: "Archive this question set",
                onClick: handleArchive,
              },
            ])}
          />
        </>
      }
    >
      <ConfirmationDialog />
      {!canUpdateAssessmentGroup && (
        <div className="m-6 p-4 border rounded-sm border-yellow-300 bg-yellow-50 mt-2 text-gray-600 text-sm">
          <div>
            This question set is already in use, so some fields cannot be
            changed. Please take care when modifying question labels as it could
            affect the historical trend analysis.
          </div>
        </div>
      )}
      <div className="p-6">
        <FormFields
          fields={[
            {
              title: "Title",
              render: () => (
                <Input
                  placeholder="Enter a title..."
                  aria-label="Assessment template title input"
                  value={proposedAssessmentGroup.title ?? ""}
                  onChange={(e) =>
                    setProposedAssessmentGroup({
                      ...proposedAssessmentGroup,
                      title: e.target.value,
                    })
                  }
                />
              ),
            },
          ]}
        />
      </div>

      {sections.map((section, sectionIndex) => {
        return (
          <AssessmentGroupSidebarSection
            key={sectionIndex}
            canUpdateAssessmentGroup={canUpdateAssessmentGroup}
            section={section}
            sectionIndex={sectionIndex}
            assessmentType={assessmentType}
            isLastSection={sectionIndex === sections.length - 1}
            deleteTooltip={
              sections.length <= 1 ? "Need at least 1 section" : undefined
            }
            onAddQuestion={(questionType) =>
              handleAddQuestion(sectionIndex, questionType)
            }
            onAddSectionBelow={() => handleAddSection(sectionIndex)}
            onRemoveSection={() => handleRemoveSection(sectionIndex)}
            onUpdateSection={(newSection) => {
              setProposedAssessmentGroup({
                ...proposedAssessmentGroup,
                sections: [
                  ...sections.slice(0, sectionIndex),
                  newSection,
                  ...sections.slice(sectionIndex + 1),
                ],
              });
            }}
            onScrollQuestionIntoView={handleScrollIntoView}
            onMoveQuestionUp={handleMoveQuestionUp}
            onMoveQuestionDown={handleMoveQuestionDown}
            onDuplicateSection={() => handleDuplicateSection(sectionIndex)}
            onMoveSectionUp={() => {
              handleMoveSection(sectionIndex, "up");
            }}
            onMoveSectionDown={() => {
              handleMoveSection(sectionIndex, "down");
            }}
          />
        );
      })}
    </Sidebar>
  );
};

export default AssessmentGroupSidebar;
